import { OktaAuth } from '@okta/okta-auth-js';
import * as Rx from 'rxjs';
import { replaceAlerts } from '../../components/alert-bar/AlertBar.state';
import { LOGOUT_REASONS, OKTA_LOGOUT_REASON_KEY } from '../constants';
import { clearLocalStorageSession, isTokenExpired } from '../state';
/*
 *** function: errorHandler
 * input:
 ** Object: {
 ** code: Int: (400, 401, 403, 404, 500) - standard HTTP error codes
 ** status: String - Standard HTTP Error Status Texts (Bad Request, Not Found, etc...)
 ** message: String - Custom cb_pos informative message for the cashier
 * }
 *
 * output:
 ** Object: {
 ** code: Int: (400, 401, 403, 404, 500)
 ** status: String: (any)
 ** message: String: (any)
 ** shouldRetry: Boolean
 ** alertType: String: ('error', 'info', 'success', 'warn')
 * }
 */
export const errorHandler = ({ code, status, message }) => {
  switch (code) {
    case 400:
    case 403:
      return Promise.reject({
        code,
        status,
        message,
        shouldRetry: false,
        alertType: 'error',
      });
    case 401:
      return Promise.reject({
        code,
        status,
        message,
        shouldRetry: false,
        alertType: 'warn',
      });
    case 404:
      return Promise.reject({
        code,
        status,
        message,
        shouldRetry: false,
        alertType: 'warn',
      });
    case 500:
      return Promise.reject({
        code,
        status,
        message,
        shouldRetry: true,
        alertType: 'error',
      });
    default:
      return Promise.reject({
        code: 500,
        status: status || 'General Error',
        message: message || 'Something went wrong',
        shouldRetry: false,
        alertType: 'info',
      });
  }
};

// TODO: noAlert param on callback instead?
// otherwise, noAlert will be set for the whole page when the callback is created
export const caughtError =
  (alertBar, heading, state, noAlert = false, errorMessageOnly = false) =>
  (error, ...otherActions) => {
    if (noAlert) return Rx.of(...otherActions);
    const { status, name, message, alertType = 'error' } = error;
    const oktaAuth = new OktaAuth(state.config.oidc);

    const is401 = error.code === 401;
    const isTerminalNotFound =
      error.message === LOGOUT_REASONS.TERMINAL_SESSION_NOT_FOUND.value;
    const shouldLogout = is401 || isTerminalNotFound;

    if (shouldLogout) {
      if (is401) {
        localStorage.setItem(
          OKTA_LOGOUT_REASON_KEY,
          isTokenExpired()
            ? LOGOUT_REASONS.EXPIRED.name
            : LOGOUT_REASONS.UNAUTHORIZED.name,
        );
      }
      if (isTerminalNotFound) {
        localStorage.setItem(
          OKTA_LOGOUT_REASON_KEY,
          LOGOUT_REASONS.TERMINAL_SESSION_NOT_FOUND.name,
        );
      }
      oktaAuth.signOut({
        postLogoutRedirectUri: window.location.origin,
      });
      clearLocalStorageSession();
    }
    const alertAction = alertBar(
      replaceAlerts({
        heading: errorMessageOnly ? message : `${heading} ${status || name}`,
        text: errorMessageOnly ? '' : message,
        variant: alertType,
      }),
    );

    return Rx.of(alertAction, ...otherActions);
  };
